import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { Autosuggest, ControlledInputChips } from '@fs/zion-ui'

import styles from './LanguagePicker.styles'
import transl from './LanguagePicker.transl'

/**
 * @param languageOptions expects an array of objects: { id:String, code:String, language:String }
 * @param selectedCodes expects an array of type String that match with "code" above
 * @param label label for selector field
 * @param onChange callback function for changes
 */
export default function LanguagePicker({
  languageOptions = [],
  selectedCodes = [],
  label = '',
  onChange = () => {},
  error,
}) {
  if (!Array.isArray(languageOptions) || !Array.isArray(selectedCodes)) {
    throw new Error('languageOptions and selectedCodes must be type "Array"')
  }
  if (typeof onChange !== 'function') {
    throw new Error('onChange must be type "function')
  }

  const [autoSuggestText, setAutoSuggestText] = useState(() => '')
  const [chips, setChips] = useState([])

  const suggestions = useMemo(
    () =>
      languageOptions?.length > 0
        ? languageOptions.map(({ id, language, code }) => ({
            key: id,
            primaryText: language,
            secondaryText: code,
          }))
        : [],
    [languageOptions]
  )

  function getPriorityMatch(input, a, b) {
    const getMatch = (foo, bar) =>
      foo.primaryText?.substring(0, input.length).toLowerCase() === input.toLowerCase() &&
      bar.primaryText?.substring(0, input.length).toLowerCase() !== input.toLowerCase()
    if (getMatch(a, b)) return -1
    if (getMatch(b, a)) return 1
    return null
  }

  function languagesFilter(input, a, b) {
    const priorityMatch = getPriorityMatch(input, a, b)
    if (priorityMatch) return priorityMatch
    const one = similarity(input, a.primaryText.toLowerCase())
    const two = similarity(input, b.primaryText.toLowerCase())

    if (one === two) {
      return a.primaryText.localeCompare(a.primaryText)
    }
    if (one > two) return -1
    return 1
  }

  function similarity(s1, s2) {
    let longer = s1
    let shorter = s2
    if (s1.length < s2.length) {
      longer = s2
      shorter = s1
    }
    const longerLength = longer.length
    if (longerLength === 0) {
      return 1.0
    }
    return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength)
  }

  function editDistance(s1, s2) {
    s1 = s1.toLowerCase()
    s2 = s2.toLowerCase()

    const costs = []
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) costs[j] = j
        else if (j > 0) {
          let newValue = costs[j - 1]
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
          costs[j - 1] = lastValue
          lastValue = newValue
        }
      }
      if (i > 0) costs[s2.length] = lastValue
    }
    return costs[s2.length]
  }

  const getChipFromLanguageCode = useCallback(
    (code) => {
      const codeCheck = languageOptions?.find((el) => el.code === code)
      return { label: codeCheck?.language || code, value: code }
    },
    [languageOptions]
  )

  function removeSelectedChip(chip) {
    const newChipList = chips.reduce((acc, current) => (chip.value === current.value ? acc : [...acc, current]), [])
    setChips(newChipList)

    onChange(newChipList.map((c) => c.value))
  }

  const handleSuggestionSelected = useCallback(
    (item) => {
      const chipList = [...chips.map((c) => c.value), item.secondaryText]
      onChange(chipList)

      setAutoSuggestText('')
      const newChip = getChipFromLanguageCode(item.secondaryText)
      setChips((v) => [...v, newChip])
    },
    [chips, getChipFromLanguageCode, onChange]
  )

  useEffect(() => {
    if (languageOptions.length > 0) setChips(selectedCodes.map((code) => getChipFromLanguageCode(code)))
  }, [getChipFromLanguageCode, languageOptions.length, selectedCodes])

  return (
    <div css={styles.selectorWrapper}>
      <Autosuggest
        label={label}
        placeholder={transl.textFieldPlaceholder()}
        inputText={autoSuggestText}
        suggestions={suggestions}
        onInputTextChange={setAutoSuggestText}
        onSuggestionSelected={handleSuggestionSelected}
        errorMessage={error && <div>This field did not save correctly.</div>}
        sortFunction={languagesFilter}
      />
      <div css={styles.chipsWrapper}>
        {languageOptions?.length > 0 && <ControlledInputChips chips={chips} onDismiss={removeSelectedChip} />}
      </div>
    </div>
  )
}
